// src/components/CookieConsentBanner.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { initAnalytics, setAnalyticsConsent } from "../lib/analytics";
import {
  CONSENT_ACCEPTED,
  CONSENT_REJECTED,
  getCookieConsent,
  setCookieConsent,
} from "../lib/consent";

export default function CookieConsentBanner() {
  const location = useLocation();
  const [consent, setConsent] = useState(() => getCookieConsent());
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    const onChanged = () => setConsent(getCookieConsent());
    const onOpen = () => setForceOpen(true);
    window.addEventListener("pc:cookie-consent-changed", onChanged);
    window.addEventListener("pc:open-cookie-settings", onOpen);
    return () => {
      window.removeEventListener("pc:cookie-consent-changed", onChanged);
      window.removeEventListener("pc:open-cookie-settings", onOpen);
    };
  }, []);

  const isVisible = forceOpen || !consent;

  // Keep router subscription for the banner, and so AnalyticsTracker can run after consent.
  void location;

  const accept = useCallback(() => {
    setCookieConsent(CONSENT_ACCEPTED);
    setAnalyticsConsent(true);
    initAnalytics();
    setForceOpen(false);
  }, []);

  const reject = useCallback(() => {
    setCookieConsent(CONSENT_REJECTED);
    setAnalyticsConsent(false);
    setForceOpen(false);
  }, []);

  const close = useCallback(() => setForceOpen(false), []);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[1000] p-3 sm:p-4">
      <div className="mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Cookies</p>
              <p className="mt-1 text-sm text-gray-700">
                We use optional analytics cookies (Google Analytics) to understand how the site is
                used and improve it. You can accept or reject analytics cookies.
              </p>
              <p className="mt-2 text-sm">
                <Link to="/privacy-policy" className="text-pink-600 underline">
                  Privacy Policy
                </Link>
              </p>
            </div>
            {forceOpen && (
              <button
                type="button"
                onClick={close}
                className="shrink-0 rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-50"
                aria-label="Close cookie settings"
              >
                Close
              </button>
            )}
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={reject}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              Reject analytics
            </button>
            <button
              type="button"
              onClick={accept}
              className="rounded-lg bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700"
            >
              Accept analytics
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
